(() => {
  const ACCORDION_SELECTOR = '.accordion > details';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const animations = new WeakMap();

  function finish(details, open) {
    details.open = open;

    details.style.height = '';
    details.style.overflow = '';

    details.classList.remove(
      'smooth-accordion--opening',
      'smooth-accordion--closing'
    );

    animations.delete(details);
  }

  function animateAccordion(details, summary, open) {
    const current = animations.get(details);

    /*
     * Start exactly from the accordion's current visible height.
     * Important when someone reverses the animation mid-way.
     */
    const startHeight = details.getBoundingClientRect().height;

    if (current) {
      current.animation.cancel();
    }

    details.style.height = '';
    details.style.overflow = '';

    let endHeight;

    if (open) {
      /*
       * <details> must technically be open before we can measure
       * its full natural height.
       */
      details.open = true;

      details.classList.remove('smooth-accordion--closing');
      details.classList.add('smooth-accordion--opening');

      endHeight = details.getBoundingClientRect().height;
    } else {
      /*
       * Keep the native <details> open while visually collapsing.
       * We remove [open] only when the animation is finished.
       */
      details.classList.remove('smooth-accordion--opening');
      details.classList.add('smooth-accordion--closing');

      endHeight = summary.getBoundingClientRect().height;
    }

    /*
     * Lock the accordion at its current visible height before
     * starting the animation.
     */
    details.style.height = `${startHeight}px`;
    details.style.overflow = 'hidden';

    const distance = Math.abs(endHeight - startHeight);

    /*
     * Duration adapts gently to content height.
     *
     * Typical PDP accordion:
     * ~430–500ms
     *
     * Very large accordion:
     * max ~620ms
     *
     * Closing is intentionally a little faster.
     */
    const openDuration = Math.min(
      620,
      Math.max(420, 360 + distance * 0.35)
    );

    const duration = open
      ? openDuration
      : Math.max(360, openDuration * 0.82);

    /*
     * Balanced ease-in-out.
     *
     * Unlike the previous strong ease-out, this doesn't blast
     * through most of the expansion during the first few frames.
     */
    const easing = open
      ? 'cubic-bezier(0.65, 0, 0.35, 1)'
      : 'cubic-bezier(0.55, 0, 0.45, 1)';

    const animation = details.animate(
      [
        { height: `${startHeight}px` },
        { height: `${endHeight}px` }
      ],
      {
        duration,
        easing
      }
    );

    animations.set(details, {
      animation,
      targetOpen: open
    });

    animation.onfinish = () => {
      const active = animations.get(details);

      /*
       * An older animation may finish after the user has already
       * reversed direction. Ignore it.
       */
      if (!active || active.animation !== animation) return;

      finish(details, open);
    };
  }

  document.addEventListener('click', (event) => {
    const summary = event.target.closest(
      '.accordion > details > summary'
    );

    if (!summary) return;

    const details = summary.parentElement;

    if (!(details instanceof HTMLDetailsElement)) return;

    /*
     * Respect the OS/browser accessibility preference.
     * Native Dawn behaviour remains intact.
     */
    if (reducedMotion.matches) return;

    event.preventDefault();

    const current = animations.get(details);

    /*
     * If currently animating, clicking again reverses direction
     * smoothly from the exact current position.
     */
    const shouldOpen = current
      ? !current.targetOpen
      : !details.open;

    summary.setAttribute(
      'aria-expanded',
      String(shouldOpen)
    );

    animateAccordion(
      details,
      summary,
      shouldOpen
    );
  });
})();